<template>
  <div>
    <PrmIconField icon-position="right">
      <PrmInputIcon class="pi pi-stopwatch" />
      <InputMaskedNumber
        :model-value="formatDurationString(modelValue)"
        style="min-width: 8rem; padding-right: 1.5rem; width: 100%"
        :class="{
          'p-invalid': invalid,
        }"
        :mask="mask"
        placeholder="000:00:00"
        :disabled="disabled"
        :readonly="readonly"
        :loading="loading"
        @change="emit('update:modelValue', parseDurationString((($event as Event).target as HTMLInputElement).value))"
      />
    </PrmIconField>
    <span
      v-if="!!errorMessage"
      class="input-wrapper-error"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>
<script lang="ts" setup>
import PrmIconField from '@NitrexGroup/primevue/iconfield';
import PrmInputIcon from '@NitrexGroup/primevue/inputicon';
import {
  computed,
  watch,
} from 'vue';

import dayjs from '../../plugins/dayjs.ts';
import InputMaskedNumber from '../input-masked-number/input-masked-number.vue';

type TModelValue = number;

export type TInputDurationUnit = 'seconds' | 'minutes' | 'hours' | 'days';

const props = withDefaults(
  defineProps<{
    modelValue: TModelValue,
    /**
     * The unit of the model value. Default: seconds
     */
    unit?: TInputDurationUnit,
    canBeNegative?: boolean,
    disabled?: boolean,
    invalid?: boolean,
    errorMessage?: string,
    readonly?: boolean,
    loading?: boolean,
  }>(),
  {
    unit: 'seconds',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: TModelValue): void,
}>();

const mask = computed(() => `${props.canBeNegative ? '-' : ''}999:59:59`);

const UNIT_FACTOR_MAP: Record<TInputDurationUnit, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
};

const unitToSecondsFactor = computed(() => UNIT_FACTOR_MAP[props.unit]);

function formatDurationString(durationSeconds: TModelValue): string {
  const duration = dayjs.duration(Math.abs(durationSeconds * unitToSecondsFactor.value), 'seconds');
  // this is the most reliable way to check for negative numbers, as this includes
  // check for negative 0.
  const isNegative = durationSeconds.toString().charAt(0) === '-';
  const sign = isNegative ? '-' : '';
  const hourString = Math.trunc(duration.asHours()).toString().padStart(3, '0');
  const minuteString = duration.minutes().toString().padStart(2, '0');
  const secondString = duration.seconds().toString().padStart(2, '0');
  return `${sign}${hourString}:${minuteString}:${secondString}`;
}

function parseDurationString(durationString: string): number {
  const [
    hours,
    minutes,
    seconds,
  ] = durationString.replace(/^-/, '').split(':');
  const sign = (durationString.charAt(0) === '-' ? -1 : 1);
  const hoursValue = parseInt(hours, 10) * 3600;
  const minutesValue = parseInt(minutes, 10) * 60;
  const secondsValue = parseInt(seconds, 10);
  return sign * (hoursValue + minutesValue + secondsValue) / unitToSecondsFactor.value;
}

watch(
  () => props.canBeNegative,
  (newValue) => {
    if (!newValue && props.modelValue.toString().startsWith('-')) {
      emit('update:modelValue', 0);
    }
  },
);
</script>
